import { useMemo, useState } from 'react'
import { useQuery } from '@apollo/client'
import { Box, Typography, Dialog, Skeleton, CircularProgress } from '@mui/material'
import { useTranslation } from 'react-i18next'
import { useSnackbar } from '../../components/Shared/useSnackbar'
import { useAppSelector } from '../../redux/store'
import {
  GET_TENANT_PARKING_POOL_CONFIG,
  IGetTenantParkingPoolConfigData,
  IGetTenantParkingPoolConfigVars,
} from '../../graphql/tenant/getTenantParkingPoolConfig'
import {
  IStatParkingPoolAvailabilityData,
  IStatParkingPoolAvailabilityVars,
  STAT_PARKING_POOL_AVAILABILITY,
} from '../../graphql/parkingPool/statParkingPoolAvailability'
import {
  periodTotals,
  todayRange,
  totalsByBuilding,
} from '../../utils/parkingPoolStats'
import { colors } from '../../const/colors'
import EmptyState from '../../components/Shared/EmptyState'
import StatValue from '../../components/Shared/StatValue'
import { dateIso2localeString } from '../../utils/formatDate'
import { downloadFile } from '../../utils/downloadFile'
import ApartmentOutlinedIcon from '@mui/icons-material/ApartmentOutlined'
import ZoomInIcon from '@mui/icons-material/ZoomInOutlined'
import MapOutlinedIcon from '@mui/icons-material/MapOutlined'

interface IBuildingStats {
  key: string
  name: string
  capacity: number
  reserved: number
  used: number
  free: number
  parkingNames: string[]
  sharedWith: string[]
  mapPdfUrl?: string | null
  mapThumbnailUrl?: string | null
}

const getBuildingStatus = (available: number, total: number) => {
  const ratio = total > 0 ? available / total : 1
  if (ratio < 0.1)
    return { labelKey: 'availability_low', bg: colors.redBg, color: colors.red }
  if (ratio < 0.25)
    return { labelKey: 'availability_medium', bg: '#FBF1CC', color: '#8a6a00' }
  return { labelKey: 'availability_good', bg: colors.greenBg, color: colors.green }
}

interface MapModalProps {
  buildingName: string
  mapPdfUrl?: string | null
  mapThumbnailUrl?: string | null
  onClose: () => void
  onToast: (msg: string) => void
}

const MapModal = ({
  buildingName,
  mapPdfUrl,
  mapThumbnailUrl,
  onClose,
  onToast,
}: MapModalProps) => {
  const { t } = useTranslation(['parking_pool_occupancy_report'])
  const [downloading, setDownloading] = useState(false)
  const fileUrl = mapPdfUrl || mapThumbnailUrl

  const handleDownload = async () => {
    if (!fileUrl || downloading) return
    setDownloading(true)
    const outcome = await downloadFile(fileUrl, buildingName)
    setDownloading(false)
    onToast(
      outcome === 'downloaded'
        ? t('map_downloaded', { name: buildingName })
        : t('map_opened_instead'),
    )
    if (outcome === 'downloaded') onClose()
  }

  return (
    <Dialog
      open
      onClose={onClose}
      maxWidth={false}
      PaperProps={{
        sx: {
          borderRadius: colors.radiusModal,
          width: 'min(1080px, 94vw)',
          maxWidth: 'none',
          p: '28px',
          boxShadow: '0 30px 80px -20px rgba(19,54,73,.35)',
        },
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', mb: '6px' }}>
        <Typography sx={{ fontSize: '20px', fontWeight: 800 }}>
          {t('map_title', { name: buildingName })}
        </Typography>
        <Box
          onClick={onClose}
          sx={{
            width: '32px', height: '32px', borderRadius: '50%',
            background: '#EAF1F4', display: 'flex', alignItems: 'center',
            justifyContent: 'center', cursor: 'pointer', color: colors.textSecondary,
            flexShrink: 0, ml: '12px',
            '&:hover': { background: '#E1E9EE' },
          }}
        >
          ×
        </Box>
      </Box>

      <Typography sx={{ fontSize: '13.5px', color: colors.textMuted, mb: '18px' }}>
        {t('map_subtitle')}
      </Typography>

      <Box
        sx={{
          background: '#EAF1F4', border: '1px solid #D5E0E7',
          borderRadius: '16px', p: '10px', mb: '20px', overflow: 'hidden',
        }}
      >
        {mapPdfUrl ? (
          <Box
            component="iframe"
            data-e2e="map-frame"
            src={mapPdfUrl}
            title={t('map_title', { name: buildingName })}
            sx={{
              width: '100%',
              height: 'min(70vh, 720px)',
              border: 'none',
              borderRadius: '10px',
              display: 'block',
              background: colors.surface,
            }}
          />
        ) : (
          <Box
            component="img"
            src={mapThumbnailUrl ?? undefined}
            alt={t('map_title', { name: buildingName })}
            sx={{
              width: '100%',
              maxHeight: 'min(70vh, 720px)',
              objectFit: 'contain',
              display: 'block',
              borderRadius: '10px',
            }}
          />
        )}
      </Box>

      {mapPdfUrl ? (
        <Box sx={{ display: 'flex', gap: '10px' }}>
        <Box
          component="a"
          href={mapPdfUrl}
          target="_blank"
          rel="noreferrer"
          sx={{
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            flex: 1, height: '48px', borderRadius: colors.radiusBtn,
            border: `1.5px solid ${colors.navy}`, color: colors.navy,
            textDecoration: 'none', fontWeight: 700, fontSize: '14px',
            transition: 'background .12s',
            '&:hover': { background: '#EAF1F4' },
          }}
        >
          {t('open_in_tab')}
        </Box>
        <Box
          onClick={handleDownload}
          sx={{
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            gap: '8px',
            flex: 1, height: '48px', borderRadius: colors.radiusBtn,
            background: 'linear-gradient(135deg,#F4D24A,#EDBD01)',
            color: colors.navy, fontWeight: 700, fontSize: '14px',
            cursor: downloading ? 'default' : 'pointer',
            userSelect: 'none',
            opacity: downloading ? 0.75 : 1,
            boxShadow: '0 12px 24px -12px rgba(237,189,1,.4)',
            transition: 'opacity .12s',
            '&:hover': { opacity: downloading ? 0.75 : 0.9 },
          }}
        >
          {downloading && (
            <CircularProgress size={15} thickness={5} sx={{ color: colors.navy }} />
          )}
          {t('download_pdf')}
        </Box>
        </Box>
      ) : (
        mapThumbnailUrl && (
          <Box sx={{ display: 'flex', gap: '10px' }}>
            <Box
              component="a"
              href={mapThumbnailUrl}
              target="_blank"
              rel="noreferrer"
              sx={{
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                flex: 1, height: '48px', borderRadius: colors.radiusBtn,
                border: `1.5px solid ${colors.navy}`, color: colors.navy,
                textDecoration: 'none', fontWeight: 700, fontSize: '14px',
                transition: 'background .12s',
                '&:hover': { background: '#EAF1F4' },
              }}
            >
              {t('open_in_tab')}
            </Box>
            <Box
              onClick={handleDownload}
              sx={{
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                gap: '8px',
                flex: 1, height: '48px', borderRadius: colors.radiusBtn,
                background: 'linear-gradient(135deg,#F4D24A,#EDBD01)',
                color: colors.navy, fontWeight: 700, fontSize: '14px',
                cursor: downloading ? 'default' : 'pointer',
                userSelect: 'none',
                opacity: downloading ? 0.75 : 1,
                boxShadow: '0 12px 24px -12px rgba(237,189,1,.4)',
                transition: 'opacity .12s',
                '&:hover': { opacity: downloading ? 0.75 : 0.9 },
              }}
            >
              {downloading && (
                <CircularProgress size={15} thickness={5} sx={{ color: colors.navy }} />
              )}
              {t('download_map_file')}
            </Box>
          </Box>
        )
      )}
    </Dialog>
  )
}

interface BuildingCardProps {
  building: IBuildingStats
  onOpenMap: (building: IBuildingStats) => void
}

const BuildingCard = ({ building, onOpenMap }: BuildingCardProps) => {
  const { t } = useTranslation(['parking_pool_occupancy_report', 'generic'])
  const { name, capacity, used, free, parkingNames, sharedWith, mapThumbnailUrl } = building
  const occupancyPct = capacity > 0 ? Math.round((used / capacity) * 100) : 0
  const status = getBuildingStatus(free, capacity)
  const [thumbnailFailed, setThumbnailFailed] = useState(false)
  const hasMap = !!mapThumbnailUrl && !thumbnailFailed

  return (
    <Box data-e2e="building-card" sx={{ background: colors.surface, border: `1px solid ${colors.border}`, borderRadius: '20px', overflow: 'hidden' }}>
      <Box
        onClick={hasMap ? () => onOpenMap(building) : undefined}
        sx={{
          position: 'relative',
          aspectRatio: '2 / 1',
          background: '#EAF1F4',
          borderBottom: `1px solid ${colors.border}`,
          overflow: 'hidden',
          cursor: hasMap ? 'pointer' : 'default',
          p: '8px',
          '&:hover img': { transform: hasMap ? 'scale(1.04)' : 'none' },
          '&:hover .map-zoom-hint': { opacity: 1 },
        }}
      >
        {hasMap ? (
          <Box
            component="img"
            src={mapThumbnailUrl!}
            alt={t('map_alt')}
            onError={() => setThumbnailFailed(true)}
            sx={{
              width: '100%',
              height: '100%',
              objectFit: 'contain',
              display: 'block',
              borderRadius: '10px',
              transition: 'transform .18s ease',
            }}
          />
        ) : (
          <Box
            sx={{
              width: '100%',
              height: '100%',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '6px',
              borderRadius: '10px',
              border: `1.5px dashed ${colors.borderDash}`,
              color: colors.slate,
              textAlign: 'center',
              p: '0 16px',
            }}
          >
            <MapOutlinedIcon sx={{ fontSize: 26 }} />
            <Box sx={{ fontSize: '13.5px', fontWeight: 800 }}>
              {thumbnailFailed ? t('map_load_error') : t('no_map')}
            </Box>
            <Box sx={{ fontSize: '11.5px', fontWeight: 600, lineHeight: 1.35 }}>
              {thumbnailFailed ? t('map_load_error_hint') : t('no_map_hint')}
            </Box>
          </Box>
        )}
        {hasMap && (
        <Box
          className="map-zoom-hint"
          sx={{
            position: 'absolute',
            bottom: '12px',
            left: '12px',
            display: 'flex',
            alignItems: 'center',
            gap: '5px',
            background: 'rgba(19,54,73,.72)',
            color: '#fff',
            fontSize: '10.5px',
            fontWeight: 700,
            p: '4px 9px',
            borderRadius: '7px',
            opacity: 0,
            transition: 'opacity .15s',
            pointerEvents: 'none',
          }}
        >
          <ZoomInIcon sx={{ fontSize: 13 }} />
          {t('map_zoom')}
        </Box>
        )}
        {hasMap && (
          <Box
            sx={{
              position: 'absolute', top: '12px', right: '12px',
              background: 'rgba(19,54,73,.72)', color: '#fff',
              fontSize: '10px', fontWeight: 700, letterSpacing: '.05em',
              p: '4px 9px', borderRadius: '7px',
            }}
          >
            {t('map_badge')}
          </Box>
        )}
      </Box>

      <Box sx={{ p: '18px 20px 20px' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '10px' }}>
          <Box sx={{ fontSize: '17px', fontWeight: 800 }}>{name}</Box>
          <Box sx={{ fontSize: '11.5px', fontWeight: 700, p: '4px 10px', borderRadius: '8px', background: status.bg, color: status.color, whiteSpace: 'nowrap' }}>
            {t(status.labelKey)}
          </Box>
        </Box>

        <Box sx={{ fontSize: '12px', color: colors.textMuted, fontWeight: 600, mt: '4px' }}>
          {t('parkings_in_building', { names: parkingNames.join(', ') })}
        </Box>

        <Box sx={{ display: 'flex', alignItems: 'baseline', gap: '6px', mt: '10px' }}>
          <Box component="span" sx={{ fontSize: '28px', fontWeight: 800, color: colors.green }}>{free}</Box>
          <Box component="span" sx={{ fontSize: '14px', color: colors.slate, fontWeight: 600 }}>
            {t('available_of', { total: capacity })}
          </Box>
        </Box>

        <Box sx={{ height: '8px', borderRadius: '6px', background: colors.border, mt: '12px', overflow: 'hidden' }}>
          <Box sx={{ height: '100%', borderRadius: '6px', width: `${Math.min(occupancyPct, 100)}%`, background: 'linear-gradient(90deg,#3f6b82,#133649)', transition: 'width .4s ease' }} />
        </Box>

        <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: '8px', fontSize: '12px', color: colors.textMuted, fontWeight: 700 }}>
          <Box component="span">{t('card_used', { used, capacity })}</Box>
          <Box component="span">{t('card_reserved', { reserved: building.reserved })}</Box>
        </Box>

        {sharedWith.length > 0 && (
          <Box
            sx={{
              mt: '12px',
              background: colors.rowSelected,
              borderRadius: colors.radiusTag,
              p: '9px 11px',
              fontSize: '11.5px',
              color: colors.navy,
              fontWeight: 700,
              lineHeight: 1.35,
            }}
          >
            {t('shared_with', { buildings: sharedWith.join(', ') })}
            <Box sx={{ fontWeight: 600, color: colors.textSecondary, mt: '2px' }}>
              {t('shared_note')}
            </Box>
          </Box>
        )}

        {hasMap && (
          <Box
            onClick={() => onOpenMap(building)}
            data-e2e="open-map"
            sx={{
              mt: '16px', display: 'flex', alignItems: 'center', justifyContent: 'center',
              gap: '8px', height: '44px', borderRadius: colors.radiusBtn,
              border: `1.5px solid ${colors.navy}`, color: colors.navy,
              fontWeight: 700, fontSize: '14px', cursor: 'pointer', userSelect: 'none',
              transition: 'background .12s',
              '&:hover': { background: '#EAF1F4' },
            }}
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M12 4v11m0 0 4-4m-4 4-4-4M5 20h14" />
            </svg>
            {t('download_map')}
          </Box>
        )}
      </Box>
    </Box>
  )
}

const ParkingOccupancyReport = () => {
  const { t, i18n } = useTranslation(['parking_pool_occupancy_report', 'generic'])
  const { tenantName } = useAppSelector(state => state.user)
  const [activeBuilding, setActiveBuilding] = useState<IBuildingStats | null>(null)
  const { notify } = useSnackbar()

  const { data, loading } = useQuery<
    IGetTenantParkingPoolConfigData,
    IGetTenantParkingPoolConfigVars
  >(GET_TENANT_PARKING_POOL_CONFIG, {
    fetchPolicy: 'cache-and-network',
    variables: { name: tenantName },
  })

  const pool = data?.getTenant?.parkingPool
  const parkingPool = pool?.id ?? ''

  const range = useMemo(() => todayRange(), [])

  const { data: statsData, error: statsError } = useQuery<
    IStatParkingPoolAvailabilityData,
    IStatParkingPoolAvailabilityVars
  >(STAT_PARKING_POOL_AVAILABILITY, {
    fetchPolicy: 'cache-and-network',
    skip: !parkingPool,
    variables: { parkingPool, ...range },
  })

  const availability = statsData?.statParkingPoolAvailability
  const stats = useMemo(() => availability?.stats ?? [], [availability])
  const poolPending = loading && !data
  const statsFailed = availability?.status === false || !!statsError
  const statsPending = !statsData && !statsError && (poolPending || !!parkingPool)

  const totals = periodTotals(stats)

  const preferEnglishMap = i18n.language.slice(0, 2) === 'en'

  const buildings = useMemo<IBuildingStats[]>(() => {
    const poolParkings = pool?.poolParkings ?? []
    const mapsByPoolParking = new Map(
      poolParkings.map(entry => [String(entry.id), entry.parking]),
    )
    const mapsByParkingName = new Map(
      poolParkings
        .filter(entry => !!entry.parking?.name)
        .map(entry => [entry.parking!.name.trim().toLowerCase(), entry.parking]),
    )

    return totalsByBuilding(stats).map(building => {
      const maps = building.parkings
        .map(
          parking =>
            mapsByPoolParking.get(String(parking.poolParkingId)) ??
            mapsByParkingName.get(parking.parkingName.trim().toLowerCase()),
        )
        .map(parking =>
          parking
            ? {
                ...parking,
                mapPdfUrl: preferEnglishMap
                  ? parking.mapPdfUrlEn || parking.mapPdfUrl
                  : parking.mapPdfUrl,
              }
            : parking,
        )
        .filter(parking => !!parking?.mapPdfUrl || !!parking?.mapThumbnailUrl)

      return {
        key: String(building.id ?? 'no-building'),
        name: building.name ?? t('no_building'),
        capacity: building.capacity,
        reserved: building.reserved,
        used: building.used,
        free: building.free,
        parkingNames: building.parkings.map(parking => parking.parkingName),
        sharedWith: building.sharedWith,
        mapPdfUrl: maps[0]?.mapPdfUrl,
        mapThumbnailUrl: maps[0]?.mapThumbnailUrl,
      }
    })
  }, [stats, pool, preferEnglishMap, t])

  if (!poolPending && !pool) {
    return (
      <EmptyState
        icon={<ApartmentOutlinedIcon sx={{ fontSize: 34, color: colors.slate }} />}
        title={t('no_parking_data')}
        description={t('no_parking_data_desc')}
      />
    )
  }

  const kpis = [
    { label: t('kpi_total_places'), value: totals.capacity, hero: true },
    { label: t('kpi_available_now'), value: totals.free, color: colors.green },
    { label: t('kpi_occupied'), value: totals.used },
    { label: t('kpi_reserved_today'), value: totals.reserved },
  ]

  return (
    <>
      <Box sx={{ fontSize: '12px', letterSpacing: '0.12em', textTransform: 'uppercase', color: colors.green, fontWeight: 800 }}>
        {t('eyebrow')}
      </Box>
      <Typography component="h1" sx={{ fontSize: '30px', fontWeight: 800, letterSpacing: '-0.02em', mt: '6px', mb: '4px', color: colors.navy, lineHeight: 1.15 }}>
        {t('title')}
      </Typography>
      <Typography sx={{ fontSize: '14.5px', color: colors.textMuted, mb: '6px' }}>
        {t('subtitle')}
      </Typography>
      <Typography sx={{ fontSize: '12.5px', color: colors.slate, fontWeight: 700, mb: '22px' }}>
        {t('as_of', {
          date: dateIso2localeString(new Date(), i18n.language, {
            year: 'numeric',
            month: '2-digit',
            day: '2-digit',
          }),
        })}
      </Typography>

      <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '14px', mb: '24px' }}>
        {kpis.map(kpi => (
          <Box
            key={kpi.label}
            sx={
              kpi.hero
                ? { background: colors.navy, borderRadius: '18px', p: '20px', color: '#fff', boxShadow: colors.shadowBtn }
                : { background: colors.surface, border: `1px solid ${colors.border2}`, borderRadius: colors.radiusCard, boxShadow: colors.shadowCard, p: '20px' }
            }
          >
            <Box sx={{ fontSize: '13px', fontWeight: 600, color: kpi.hero ? 'inherit' : colors.textMuted, opacity: kpi.hero ? 0.92 : 1 }}>
              {kpi.label}
            </Box>
            <Box sx={{ fontSize: '30px', fontWeight: 800, mt: '6px', color: kpi.color }}>
              <StatValue
                loading={statsPending}
                error={statsFailed}
                errorMessage={t('stats_error')}
                empty={stats.length === 0}
                onDark={kpi.hero}
                height={36}
              >
                {kpi.value}
              </StatValue>
            </Box>
          </Box>
        ))}
      </Box>

      {statsPending ? (
        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '18px' }}>
          {[0, 1, 2].map(card => (
            <Skeleton
              key={card}
              variant="rounded"
              height={392}
              sx={{ borderRadius: '20px' }}
            />
          ))}
        </Box>
      ) : buildings.length > 0 ? (
        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '18px' }}>
          {buildings.map(building => (
            <BuildingCard
              key={building.key}
              building={building}
              onOpenMap={setActiveBuilding}
            />
          ))}
        </Box>
      ) : (
        <Box
          sx={{
            background: statsFailed ? colors.redBg : colors.surface,
            border: `1px solid ${statsFailed ? colors.redBorder : colors.border2}`,
            borderRadius: colors.radiusCard,
            p: '40px',
            textAlign: 'center',
            color: statsFailed ? colors.redDark : colors.textMuted,
            fontWeight: 700,
          }}
        >
          {statsFailed ? t('stats_error') : t('no_stats')}
        </Box>
      )}

      {activeBuilding && (
        <MapModal
          buildingName={activeBuilding.name}
          mapPdfUrl={activeBuilding.mapPdfUrl}
          mapThumbnailUrl={activeBuilding.mapThumbnailUrl}
          onClose={() => setActiveBuilding(null)}
          onToast={notify}
        />
      )}

    </>
  )
}

export default ParkingOccupancyReport
